import { Delete, PhotoCamera } from '@mui/icons-material';
import {
    Alert,
    Box,
    IconButton,
    Paper,
    Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';

const ImageUpload = ({
    label = "Upload de imagem",
    value = null,
    onChange,
    accept = "image/*",
    maxSize = 5 * 1024 * 1024, // 5MB por padrão
    error = null,
    disabled = false,
    compressImage = true, // Habilitar compressão por padrão
    maxWidth = 1920, // Largura máxima para compressão
    maxHeight = 1080, // Altura máxima para compressão
    quality = 0.8 // Qualidade da compressão (0.1 a 1.0)
}) => {
    const [dragActive, setDragActive] = useState(false);
    const [preview, setPreview] = useState(value);
    const fileInputRef = useRef(null);

    // Atualiza preview quando value muda externamente
    React.useEffect(() => {
        setPreview(value);
    }, [value]);

    // Função para comprimir imagem
    const compressImageFile = (file) => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calcular novas dimensões mantendo proporção
                let { width, height } = img;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = width * ratio;
                    height = height * ratio;
                }

                // Configurar canvas com novas dimensões
                canvas.width = width;
                canvas.height = height;

                // Desenhar imagem redimensionada
                ctx.drawImage(img, 0, 0, width, height);

                // Converter para blob com qualidade especificada
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            // Criar novo arquivo com o blob comprimido
                            const compressedFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        } else {
                            reject(new Error('Erro ao comprimir imagem'));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };

            img.onerror = () => reject(new Error('Erro ao carregar imagem'));
            img.src = URL.createObjectURL(file);
        });
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            handleFile(files[0]);
        }
    };

    const handleFileInput = (e) => {
        if (disabled) return;

        const files = e.target.files;
        if (files && files[0]) {
            handleFile(files[0]);
        }
    };

    const handleFile = async (file) => {
        // Validação do tipo de arquivo
        if (!file.type.startsWith('image/')) {
            alert('Por favor, selecione apenas arquivos de imagem.');
            return;
        }

        try {
            let processedFile = file;
            let originalSize = file.size;

            // Comprimir imagem se habilitado
            if (compressImage) {
                try {
                    processedFile = await compressImageFile(file);
                    const compressedSize = processedFile.size;
                    const compressionRatio = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);

                    console.log(`Imagem comprimida: ${Math.round(originalSize / 1024)}KB → ${Math.round(compressedSize / 1024)}KB (${compressionRatio}% de redução)`);
                } catch (compressionError) {
                    console.warn('Erro na compressão, usando arquivo original:', compressionError);
                    // Se houver erro na compressão, usar arquivo original
                }
            }

            // Validação do tamanho após compressão
            if (processedFile.size > maxSize) {
                alert(`O arquivo ainda é muito grande após compressão: ${Math.round(processedFile.size / 1024)}KB (máximo: ${Math.round(maxSize / 1024)}KB)`);
                return;
            }

            // Cria preview da imagem
            const reader = new FileReader();
            reader.onload = (e) => {
                const result = e.target.result;
                setPreview(result);
                onChange && onChange(processedFile, result);
            };
            reader.readAsDataURL(processedFile);

        } catch (error) {
            console.error('Erro ao processar arquivo:', error);
            alert('Erro ao processar o arquivo. Tente novamente.');
        }
    };

    const handleRemove = () => {
        setPreview(null);
        onChange && onChange(null, null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClick = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                {label}
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
                    backgroundColor: dragActive ? '#f5f5f5' : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': disabled ? {} : {
                        borderColor: '#1976d2',
                        backgroundColor: '#f5f5f5'
                    }
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInput}
                    style={{ display: 'none' }}
                    disabled={disabled}
                />

                {preview ? (
                    <Box>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <img
                                src={preview}
                                alt="Preview"
                                style={{
                                    maxWidth: '200px',
                                    maxHeight: '200px',
                                    objectFit: 'contain',
                                    borderRadius: '4px'
                                }}
                            />
                            {!disabled && (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemove();
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: -10,
                                        right: -10,
                                        backgroundColor: 'error.main',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'error.dark'
                                        }
                                    }}
                                    size="small"
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            Clique para alterar a imagem
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <PhotoCamera sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                            {disabled ? 'Upload desabilitado' : 'Tire uma foto ou selecione uma imagem da galeria'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Formatos aceitos: JPG, PNG
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Tamanho máximo: {Math.round(maxSize / (1024 * 1024))}MB
                        </Typography>
                        {compressImage && (
                            <Typography variant="body2" color="textSecondary">
                                Compressão automática: {Math.round(maxWidth)}x{Math.round(maxHeight)}px, {Math.round(quality * 100)}% qualidade
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
};

export default ImageUpload;
